import { type Action, type ThunkDispatch } from '@reduxjs/toolkit';
import { type AbiItem } from 'web3-utils';

import { Emojis, type ErrorResource, Events, NFTClient, NftMintSteps, type ResourceId } from '@app/client';
import { apiFactory } from '@app/client/apiFactory/api';
import config from '@app/config';
import { prefetch2FA } from '@app/helpers';

import { type RootState } from '../reducers';
import {
    ACTION_NFT_MINT_PANEL_STEP,
    ACTION_SET_LIQUID_ID_COLLECTION_ID,
    ACTION_SET_NFT_PANEL_OPEN,
    ACTION_SET_OPENSEA_URL,
    type NftActionTypes,
} from '../types/nft';

import { registerEvent } from './events';
import { addNotification } from './notification';

const { nftOpenseaLink, tokenContractAddress } = config; // todo

export const setLiquidIdCollectionId = (liquidIdCollectionId: ResourceId): NftActionTypes => ({
    type: ACTION_SET_LIQUID_ID_COLLECTION_ID,
    liquidIdCollectionId,
});

export const setNftPanelOpen = (nftPanelOpen: boolean): NftActionTypes => ({
    type: ACTION_SET_NFT_PANEL_OPEN,
    nftPanelOpen,
});

export const setNftMintPanelStep = (nftMintPanelStep: NftMintSteps | null): NftActionTypes => ({
    type: ACTION_NFT_MINT_PANEL_STEP,
    nftMintPanelStep,
});

export const setOpenSeaUrl = (openSeaUrl: string | null): NftActionTypes => ({
    type: ACTION_SET_OPENSEA_URL,
    openSeaUrl,
});

export const openNftPanel = (nftMintPanelStep?: NftMintSteps) => {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>): Promise<void> {
        if (nftMintPanelStep) {
            dispatch(setNftMintPanelStep(nftMintPanelStep));
        }
        dispatch(setNftPanelOpen(true));
    };
};

export const loadCurrentEmojiOpenSeaUrl = () => {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>, getState: () => RootState): Promise<void> {
        const currentEmojiTokenId = getState().userEmojis.currentEmojiTokenId;
        const url = [nftOpenseaLink];
        if (currentEmojiTokenId) {
            url.push(`/${tokenContractAddress}/${currentEmojiTokenId}`); //todo - get
        }
        dispatch(setOpenSeaUrl(url.join('')));
    };
};

export const watchAsset = (contractAddress = tokenContractAddress) => {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>): Promise<void> {
        try {
            // wasAdded is a boolean. Like any RPC method, an error may be thrown.
            await window.ethereum.request({
                method: 'wallet_watchAsset',
                params: {
                    type: 'ERC20', // Initially only supports ERC20, but eventually more!
                    options: {
                        address: contractAddress, // The address that the token is at.
                        symbol: 'Yats', // A ticker symbol or shorthand, up to 5 chars.
                        decimals: 0,
                        image: 'https://y.at/assets/img/yat-hand-purple-outline.svg', // logo
                    },
                },
            });
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
            dispatch(addNotification({ severity: 'error', message: error.message }));
        }
    };
};

export function mintYatNft(nftName: string) {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>, getState: () => RootState): Promise<void> {
        const chainFormat = getState().userEmojis.currentEmojiChainFormat;
        const currentEmoji = getState().userEmojis.currentEmoji;

        if (currentEmoji) {
            dispatch(setNftMintPanelStep(NftMintSteps.LoadingMint));
            localStorage.setItem('mintingInitiated', '1'); // because the 2fa auth calls initializeUser which reset the panel state
            try {
                try {
                    const result = await apiFactory().call(NFTClient.NFT.setName, {
                        value: { data: { name: nftName } },
                        eid: currentEmoji,
                    });
                    // TODO: Check if data is already attached, which will cause API response to return 422
                    if (!result) {
                        dispatch(setNftMintPanelStep(NftMintSteps.Error));
                    }
                } catch (error) {
                    dispatch(setNftMintPanelStep(NftMintSteps.Error));
                }

                // Start minting process
                const token = chainFormat || currentEmoji;
                const from = window.ethereum.selectedAddress;

                const web3module = await import('web3');
                const Web3 = web3module.default;
                const web3 = new Web3(window.ethereum);

                const module = await import('@pkg/assets/contracts/YAT.json');
                const YAT = module.default;

                const YatContract = new web3.eth.Contract(YAT.abi as AbiItem[], tokenContractAddress);

                // Use this to detect if wallet connected to Mainnet network
                // const walletNetworkId = await web3.eth.net.getId();
                // if (walletNetworkId !== 1) {
                //     // Metamask not connected to Mainnet
                // }

                const signingResult = await apiFactory().call(Emojis.Emoji.sign, {
                    prefetch: prefetch2FA(dispatch, true),
                    value: { account: from },
                    eid: currentEmoji,
                });

                if (signingResult.ok) {
                    const expiry = signingResult.value.expiry;
                    const signature = signingResult.value.signature;

                    const result = await YatContract.methods.mint(token, from, expiry, signature).send({ from });
                    if (result.status) {
                        localStorage.removeItem('mintingInitiated');
                        dispatch(await watchAsset());
                        dispatch(setNftMintPanelStep(NftMintSteps.YatMintedFeedback));
                        registerEvent(Events.yat_minted, { yat_unicode: currentEmoji });

                        // Set OpenSea URL for the confirmation screen
                        const userBalance = await YatContract.methods.balanceOf(from).call({ from });
                        if (userBalance > 0) {
                            const lastTokenId = await YatContract.methods
                                .tokenOfOwnerByIndex(from, userBalance - 1)
                                .call({ from });
                            const url = `${nftOpenseaLink}/${tokenContractAddress}/${lastTokenId}`;
                            dispatch(setOpenSeaUrl(url));
                        }

                        setTimeout(function () {
                            dispatch(setNftMintPanelStep(NftMintSteps.Complete));
                        }, 2 * 1000); // 2 seconds
                    }
                } else {
                    localStorage.removeItem('mintingInitiated');
                    dispatch(setNftMintPanelStep(NftMintSteps.Error));
                }
            } catch (e) {
                const error = e as ErrorResource;
                // User rejected signature on MetaMask
                if (error && error.code && error.code === 4001) {
                    // TODO: Remove this once loading screen is only started upon minting event
                    dispatch(setNftMintPanelStep(NftMintSteps.NameNft));
                    localStorage.removeItem('mintingInitiated');
                } else {
                    dispatch(setNftMintPanelStep(NftMintSteps.Error));
                    localStorage.removeItem('mintingInitiated');
                }
            }
        }
    };
}

export const openRenameYatSidePanel = () => {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>): Promise<void> {
        dispatch(setNftMintPanelStep(NftMintSteps.RenameNft));
        dispatch(setNftPanelOpen(true));
    };
};

export function setNewNftName(newNftName: string) {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>, getState: () => RootState): Promise<void> {
        const currentEmoji = getState().userEmojis.currentEmoji;
        await apiFactory()
            .call(NFTClient.NFT.setName, {
                value: { data: { name: newNftName } },
                eid: currentEmoji,
            })
            .then((r) => {
                if (r.ok) {
                    dispatch(setNftMintPanelStep(NftMintSteps.RenameNftSuccess));
                } else {
                    dispatch(setNftMintPanelStep(NftMintSteps.Error));
                }
            })
            .catch(() => {
                dispatch(setNftMintPanelStep(NftMintSteps.Error));
            });
    };
}
